import { useState, useEffect, useRef } from "react";
import { useSocketIo } from "@/hooks/useSocketIo";
import { API , authFetch, useEditMode, useAuth } from "@/App";
import { toast } from "sonner";
import { ApprovalActionDialog, canApprove } from "@/components/ApprovalActionDialog";
import { SyncNowButton } from "@/components/SyncNowButton";
import { mutate } from "@/lib/apiMutate";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import {
  ShoppingCart,
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Check,
  X,
  Truck,
  Package,
  Clock,
  Tag,
  AlertTriangle,
  CheckCircle,
  Camera,
  RefreshCw,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OrderTimeline from "@/components/OrderTimeline";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import { SalesmanPerformancePanel } from "@/components/SalesmanPerformancePanel";

const OrdersPage = () => {
  const canEdit = useEditMode();
  const { user } = useAuth();
  const canApproveOrders = canApprove(user);
  const [orders, setOrders] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [activeRegion, setActiveRegion] = useState(() =>
    isSuperAdmin
      ? (localStorage.getItem("pravaah_region") || "ALL")
      : (user?.region_id || "ALL")
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("orders"); // "orders" | "performance"
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [timelineOrderId, setTimelineOrderId] = useState(null);
  const [orderTags, setOrderTags] = useState([]);
  const [showTagForm, setShowTagForm] = useState(false);
  const [tagForm, setTagForm] = useState({ label: "", note: "", tag_type: "issue" });
  const [tagPhotos, setTagPhotos] = useState([]);
  const [approvalDialog, setApprovalDialog] = useState(null); // { order, action: 'APPROVE'|'REJECT'|'HOLD' }
  const [approvalForm, setApprovalForm] = useState({ tag: "", notes: "" });
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState(null);
  const [formData, setFormData] = useState({
    party_id: "",
    delivery_address: "",
    target_unit: "unit_1",
    items: [{ product_id: "", qty: 1 }],
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from DispatchQueue / FactoryDashboard: ?highlight=<order_id> auto-opens
  // the order timeline so admin sees the full audit trail without manual navigation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlight = params.get("highlight");
    if (highlight && orders.length > 0) {
      const target = orders.find((o) => o.order_id === highlight);
      if (target) {
        setTimelineOrderId(highlight);
      }
    }
  }, [orders]);

  // Auto-refresh: refetch on tab visibility change (when user comes back to the tab).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchData({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh: silent poll every 30s while the tab is visible.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchData({ silent: true });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time: subscribe to Socket.IO and refetch on order_* / approval_* / dispatch_*
  // events so a salesman's mobile order pops into the table within ~1 second.
  // Migrated from raw WebSocket → Socket.IO singleton (P1 iter 73).
  useSocketIo({
    realtime_update: (payload) => {
      const t = String(payload?.event || "").toLowerCase();
      if (t.includes("order") || t.includes("approval") || t.includes("dispatch")) {
        fetchData({ silent: true });
      }
    },
    notification: (payload) => {
      const t = String(payload?.notification_type || payload?.type || "").toLowerCase();
      if (t.includes("order") || t.includes("approval") || t.includes("dispatch")) {
        fetchData({ silent: true });
      }
    },
    order_created: () => fetchData({ silent: true }),
    order_status_changed: () => fetchData({ silent: true }),
  }, { enabled: !!user?.user_id });

  // Always pulls FULL latest list (limit=200) — status & search filtering happen
  // client-side so per-tab counts stay correct and search is instant.
  const fetchData = async ({ silent = false } = {}) => {
    try {
      const url = `${API}/orders?limit=200&_=${Date.now()}`;

      const ordersRes = await authFetch(url, { cache: "no-store" });
      if (!ordersRes.ok) {
        throw new Error(`HTTP ${ordersRes.status}`);
      }
      const ordersData = await ordersRes.json();
      if (ordersData.success) {
        const list = Array.isArray(ordersData.data) ? [...ordersData.data] : [];
        // Defensive client-side sort: newest first by created_at, fallback to order_date.
        list.sort((a, b) => {
          const ta = new Date(a.created_at || a.order_date || 0).getTime();
          const tb = new Date(b.created_at || b.order_date || 0).getTime();
          return tb - ta;
        });
        setOrders(list);
        setLastUpdatedAt(new Date());
      } else {
        throw new Error(ordersData.message || "Orders API returned success=false");
      }

      // Fetch ALL parties (not just APPROVED), products, and employees so we can
      // resolve dealer name + salesman name for every order row.
      const [partiesRes, productsRes, employeesRes] = await Promise.all([
        authFetch(`${API}/parties?limit=500`, {  }),
        authFetch(`${API}/products?status=ACTIVE`, {  }),
        authFetch(`${API}/employees?limit=500`, {  }),
      ]);

      const partiesData = await partiesRes.json();
      const productsData = await productsRes.json();
      const employeesData = await employeesRes.json();

      if (partiesData.success) setParties(partiesData.data);
      if (productsData.success) setProducts(productsData.data);
      if (employeesData.success) setEmployees(employeesData.data || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      if (!silent) {
        toast.error(`Failed to load orders: ${error.message || "network error"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      toast.success("Orders refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = formData.items.map((item) => {
      const product = products.find((p) => p.product_id === item.product_id);
      return {
        product_id: item.product_id,
        sku: product?.sku_code,
        product_name: product?.product_name,
        qty: parseInt(item.qty),
        unit_price: product?.base_price || 0,
        discount: 0,
      };
    });
    const party = parties.find((p) => p.party_id === formData.party_id);
    const r = await mutate(
      authFetch,
      `${API}/orders`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_date: new Date().toISOString().split("T")[0],
          party_id: formData.party_id,
          delivery_address: formData.delivery_address || party?.address || "",
          target_unit: formData.target_unit || "unit_1",
          items,
        }),
      },
      { successMessage: "Order created successfully", failureMessage: "Failed to create order" },
    );
    if (!r.ok) return;
    setIsDialogOpen(false);
    resetForm();
    fetchData();
  };

  const handleStatusUpdate = async (orderId, status, reason = "") => {
    const r = await mutate(
      authFetch,
      `${API}/orders/${orderId}/status?status=${status}&reason=${encodeURIComponent(reason)}`,
      { method: "PATCH" },
      { successMessage: `Order ${status.toLowerCase()}`, failureMessage: "Failed to update order status" },
    );
    if (r.ok) fetchData();
  };

  const APPROVAL_REASON_TAGS = [
    "Stock Available", "Credit OK", "Price Verified", "Scheme Eligible",
    "Stock Unavailable", "Price Mismatch", "Payment Pending", "Credit Limit Exceeded",
    "Customer Request", "Scheme Violation", "Quantity Issue", "Documentation Pending",
    "Duplicate Order", "Awaiting Clarification", "Other",
  ];

  const openApprovalDialog = (order, action) => {
    setApprovalDialog({ order, action });
    setApprovalForm({ tag: "", notes: "" });
  };

  const submitApprovalAction = async () => {
    if (!approvalDialog) return;
    if (!approvalForm.tag) {
      toast.error("Please select a reason tag");
      return;
    }
    const { order, action } = approvalDialog;
    const endpoint = action === "APPROVE" ? "approve" : action === "REJECT" ? "reject" : "hold";
    setApprovalSaving(true);
    const r = await mutate(
      authFetch,
      `${API}/orders/${order.order_id}/${endpoint}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag: approvalForm.tag, notes: approvalForm.notes }),
      },
      {
        successMessage: `Order ${action.toLowerCase()}d with tag: ${approvalForm.tag}`,
        failureMessage: "Action failed",
      },
    );
    setApprovalSaving(false);
    if (r.ok) {
      setApprovalDialog(null);
      fetchData();
    }
  };

  const resetForm = () => {
    setFormData({
      party_id: "",
      delivery_address: "",
      target_unit: "unit_1",
      items: [{ product_id: "", qty: 1 }],
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: "", qty: 1 }],
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const formatCurrency = (rupees) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(rupees || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      PLACED: { class: "badge-info", icon: Clock },
      APPROVED: { class: "badge-success", icon: Check },
      ON_HOLD: { class: "badge-warning", icon: Clock },
      DISPATCHED: { class: "bg-purple-100 text-purple-700 border-purple-200", icon: Truck },
      DELIVERED: { class: "badge-success", icon: Package },
      CANCELLED: { class: "badge-danger", icon: X },
    };
    const config = styles[status] || styles.PLACED;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  // Party lookup map for O(1) dealer name resolution
  const partyById = parties.reduce((acc, p) => {
    if (p?.party_id) acc[p.party_id] = p;
    return acc;
  }, {});

  // Employee lookup — match by employee_id, user_id, or employee_code so orders
  // placed via mobile (employee_id) and admin (user_id) both resolve.
  const employeeByKey = employees.reduce((acc, e) => {
    if (e?.employee_id) acc[e.employee_id] = e;
    if (e?.user_id) acc[e.user_id] = e;
    if (e?.employee_code) acc[e.employee_code] = e;
    return acc;
  }, {});
  const resolveSalesmanName = (order) => {
    // Prefer denormalized names stamped at creation time (mobile orders).
    if (order.salesperson_name) return order.salesperson_name;
    if (order.placed_by_name && order.placed_by_name !== "Admin") return order.placed_by_name;
    const keys = [order.salesperson_id, order.placed_by].filter(Boolean);
    for (const k of keys) {
      const emp = employeeByKey[k];
      if (emp) return emp.full_name || emp.name || emp.email;
    }
    return order.placed_by_name || (order.placed_via === "ADMIN" ? "Admin" : (order.salesperson_id || order.placed_by || "—"));
  };

  // Pre-filter by region for counts — so when admin switches to GJ, all tab counts
  // reflect only GJ orders. Keeps counts in sync with what the table shows.
  const regionFilteredOrders = orders.filter((o) => {
    if (!activeRegion || activeRegion === "ALL") return true;
    const dealer = partyById[o.party_id];
    const empKeys = [o.salesperson_id, o.placed_by].filter(Boolean);
    const emp = empKeys.map((k) => employeeByKey[k]).find(Boolean);
    const orderRegion = o.region_id || dealer?.region_id || emp?.region_id;
    return orderRegion === activeRegion;
  });

  const statusCounts = regionFilteredOrders.reduce(
    (acc, o) => {
      acc.all += 1;
      if (acc[o.status] !== undefined) acc[o.status] += 1;
      return acc;
    },
    { all: 0, PLACED: 0, ON_HOLD: 0, APPROVED: 0, DISPATCHED: 0, DELIVERED: 0, CANCELLED: 0 },
  );

  // Client-side filter for the table view (counts above always reflect the full list).
  // Supports a virtual "REVIEW" filter that bundles PLACED + ON_HOLD for the
  // "Review Now" CTA, full-text search, and region filtering.
  const searchLower = (search || "").trim().toLowerCase();
  const visibleOrders = regionFilteredOrders.filter((o) => {
    // status filter
    if (statusFilter && statusFilter !== "all" && statusFilter !== "") {
      if (statusFilter === "REVIEW") {
        if (o.status !== "PLACED" && o.status !== "ON_HOLD") return false;
      } else if (o.status !== statusFilter) {
        return false;
      }
    }
    // search filter (order_id / party_id / dealer firm_name / contact_person / salesman name)
    if (searchLower) {
      const dealer = partyById[o.party_id];
      const haystack = [
        o.order_id,
        o.party_id,
        dealer?.firm_name,
        dealer?.contact_person,
        resolveSalesmanName(o),
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(searchLower)) return false;
    }
    return true;
  });

  const TAG_LABELS = ["Delayed", "Payment Pending", "Quality Dispute", "Quantity Mismatch", "Wrong Product", "Damaged Goods", "Other"];

  const fetchOrderTags = async (orderId) => {
    try {
      const res = await authFetch(`${API}/orders/${orderId}/tags`);
      const data = await res.json();
      if (data.success) setOrderTags(data.data || []);
    } catch { setOrderTags([]); }
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    fetchOrderTags(order.order_id);
    setShowTagForm(false);
    setTagForm({ label: "", note: "", tag_type: "issue" });
    setTagPhotos([]);
  };

  const addTag = async () => {
    if (!tagForm.label || !viewingOrder) return;
    try {
      let photos = [];
      if (tagPhotos.length > 0) {
        const fd = new FormData();
        tagPhotos.forEach(f => fd.append("files", f));
        const upRes = await authFetch(`${API}/upload-multiple?category=order-tags`, { method: "POST", body: fd });
        const upData = await upRes.json();
        if (upData.success) photos = upData.data.map(d => d.storage_path);
      }
      const res = await authFetch(`${API}/orders/${viewingOrder.order_id}/tags`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...tagForm, photos }),
      });
      if ((await res.json()).success) {
        toast.success("Tag added");
        fetchOrderTags(viewingOrder.order_id);
        setShowTagForm(false);
        setTagForm({ label: "", note: "", tag_type: "issue" });
        setTagPhotos([]);
      }
    } catch { toast.error("Failed to add tag"); }
  };

  const resolveTag = async (tagId) => {
    if (!viewingOrder) return;
    try {
      await authFetch(`${API}/orders/${viewingOrder.order_id}/tags/${tagId}/resolve`, { method: "PUT" });
      toast.success("Tag resolved");
      fetchOrderTags(viewingOrder.order_id);
    } catch { toast.error("Failed to resolve tag"); }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 font-['Sora']">Orders</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage sales orders</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={(r) => {
              setActiveRegion(r);
              if (isSuperAdmin) localStorage.setItem("pravaah_region", r);
            }}
            isSuperAdmin={isSuperAdmin}
            dataTestId="orders-region-switcher"
          />
          {lastUpdatedAt && (
            <span
              className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline"
              data-testid="orders-last-updated"
              title={lastUpdatedAt.toString()}
            >
              Last updated {lastUpdatedAt.toLocaleTimeString("en-IN", { hour12: false })}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            data-testid="refresh-orders-btn"
            title="Refresh to load latest orders"
            className="border-2 border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white dark:border-rose-500 dark:text-rose-400 dark:hover:bg-rose-500 dark:hover:text-white shadow-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <SyncNowButton entity="orders" />
          {canEdit && (
            <Button
              onClick={() => {
                resetForm();
                setIsDialogOpen(true);
              }}
              className="btn-primary"
              data-testid="create-order-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Order
            </Button>
          )}
        </div>
      </div>

      {/* Pending Approvals Alert — prominent call to action for approvers */}
      {canApproveOrders && ((statusCounts.PLACED || 0) + (statusCounts.ON_HOLD || 0)) > 0 && (
        <Card className="p-4 border-2 border-amber-300 dark:border-amber-600 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 shadow-sm" data-testid="pending-orders-alert">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900 dark:text-amber-100 text-base">
                {(statusCounts.PLACED || 0) + (statusCounts.ON_HOLD || 0)} orders need your action
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200">
                <strong>{statusCounts.PLACED || 0}</strong> placed · <strong>{statusCounts.ON_HOLD || 0}</strong> on hold — click Review to approve, hold, or reject with a reason tag.
              </p>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white shadow"
              onClick={() => {
                setStatusFilter("REVIEW");
                // ensure the table is in view
                setTimeout(() => {
                  document.querySelector('[data-testid="orders-table"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 50);
              }}
              data-testid="review-pending-orders"
            >
              Review Now →
            </Button>
          </div>
        </Card>
      )}

      {/* View Mode (Orders / Salesman Performance) */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full" data-testid="orders-view-tabs">
        <TabsList className="bg-slate-100 dark:bg-slate-800 grid grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="orders" data-testid="view-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="performance" data-testid="view-tab-performance">Salesman Performance</TabsTrigger>
        </TabsList>
      </Tabs>

      {viewMode === "performance" ? (
        <SalesmanPerformancePanel
          orders={orders}
          employees={employees}
          activeRegion={activeRegion}
        />
      ) : (
      <>
      {/* Status Tabs */}
      <Tabs value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({statusCounts.all})
          </TabsTrigger>
          <TabsTrigger value="REVIEW" data-testid="tab-review">
            Needs Action ({(statusCounts.PLACED || 0) + (statusCounts.ON_HOLD || 0)})
          </TabsTrigger>
          <TabsTrigger value="PLACED" data-testid="tab-placed">
            Pending ({statusCounts.PLACED})
          </TabsTrigger>
          <TabsTrigger value="ON_HOLD" data-testid="tab-on-hold">
            On Hold ({statusCounts.ON_HOLD})
          </TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="tab-approved">
            Approved ({statusCounts.APPROVED})
          </TabsTrigger>
          <TabsTrigger value="DISPATCHED" data-testid="tab-dispatched">
            Dispatched ({statusCounts.DISPATCHED})
          </TabsTrigger>
          <TabsTrigger value="DELIVERED" data-testid="tab-delivered">
            Delivered ({statusCounts.DELIVERED})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <Input
            placeholder="Search by order ID, dealer name, or party ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="order-search"
          />
        </div>
      </Card>

      {/* Orders Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Dealer</th>
                <th>Ordered By</th>
                <th>Items</th>
                <th className="text-right">Amount</th>
                <th>Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : visibleOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="empty-state">
                      <ShoppingCart className="empty-state-icon" />
                      <p className="empty-state-title">No orders found</p>
                      <p className="empty-state-description">
                        Create your first order to get started.
                      </p>
                      <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        New Order
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                visibleOrders.map((order) => {
                  const party = partyById[order.party_id];
                  const salesmanName = resolveSalesmanName(order);
                  return (
                    <tr
                      key={order.order_id}
                      data-testid={`order-row-${order.order_id}`}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                      onClick={(e) => {
                        // Don't open detail if the user clicked on an interactive cell
                        const tag = (e.target.tagName || "").toLowerCase();
                        if (["button", "svg", "path", "input", "a"].includes(tag)) return;
                        if (e.target.closest("button, a, [role='menu'], [role='menuitem']")) return;
                        setDetailOrderId(order.order_id);
                      }}
                    >
                      <td className="font-mono text-sm font-medium">
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setDetailOrderId(order.order_id); }}
                          className="text-left hover:underline text-[#8B1A1A]"
                          data-testid={`view-order-btn-${order.order_id}`}
                        >
                          {order.order_id}
                        </button>
                      </td>
                      <td>{order.order_date}</td>
                      <td>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {party?.firm_name || party?.contact_person || order.party_id || "-"}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{party?.contact_person}</p>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100" data-testid={`order-salesman-${order.order_id}`}>
                            {salesmanName}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {order.placed_via === "EMPLOYEE_APP" ? "Mobile" : (order.placed_via || "Admin")}
                          </p>
                        </div>
                      </td>
                      <td>{order.items?.length || 0} items</td>
                      <td className="numeric font-bold">
                        {formatCurrency(order.total_amount)}
                      </td>
                      <td>{getStatusBadge(order.status)}
                        {order.tags?.filter(t => !t.resolved).length > 0 && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 border border-red-200">
                            <AlertTriangle className="w-2.5 h-2.5" />{order.tags.filter(t => !t.resolved).length}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {/* Inline approval icons — always visible to approval roles */}
                          {canApproveOrders && (order.status === "PLACED" || order.status === "ON_HOLD") && (
                            <>
                              <Button
                                variant="ghost" size="sm"
                                className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                onClick={() => openApprovalDialog(order, "APPROVE")}
                                data-testid={`inline-approve-${order.order_id}`}
                                title="Approve"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              {order.status === "PLACED" && (
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                  onClick={() => openApprovalDialog(order, "HOLD")}
                                  data-testid={`inline-hold-${order.order_id}`}
                                  title="Put on Hold"
                                >
                                  <Clock className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => openApprovalDialog(order, "REJECT")}
                                data-testid={`inline-reject-${order.order_id}`}
                                title="Reject"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canApproveOrders && order.status === "APPROVED" && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-50"
                              onClick={() => openApprovalDialog(order, "APPROVE")}
                              data-testid={`inline-dispatch-${order.order_id}`}
                              title="Mark Dispatched"
                            >
                              <Truck className="w-4 h-4" />
                            </Button>
                          )}
                          {canApproveOrders && order.status === "DISPATCHED" && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                              onClick={() => openApprovalDialog(order, "APPROVE")}
                              data-testid={`inline-deliver-${order.order_id}`}
                              title="Mark Delivered"
                            >
                              <Package className="w-4 h-4" />
                            </Button>
                          )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`order-menu-${order.order_id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setDetailOrderId(order.order_id)} data-testid={`view-order-${order.order_id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setTimelineOrderId(order.order_id)} data-testid={`timeline-${order.order_id}`}>
                              <Clock className="w-4 h-4 mr-2" />
                              View Timeline
                            </DropdownMenuItem>
                            {canApproveOrders && order.status === "PLACED" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => openApprovalDialog(order, "APPROVE")}
                                  className="text-emerald-600"
                                  data-testid={`approve-order-${order.order_id}`}
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  Approve (with tag)
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openApprovalDialog(order, "HOLD")}
                                  className="text-amber-600"
                                  data-testid={`hold-order-${order.order_id}`}
                                >
                                  <Clock className="w-4 h-4 mr-2" />
                                  Put on Hold (with tag)
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openApprovalDialog(order, "REJECT")}
                                  className="text-red-600"
                                  data-testid={`reject-order-${order.order_id}`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Reject (with tag)
                                </DropdownMenuItem>
                              </>
                            )}
                            {canApproveOrders && order.status === "ON_HOLD" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => openApprovalDialog(order, "APPROVE")}
                                  className="text-emerald-600"
                                  data-testid={`approve-order-${order.order_id}`}
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  Approve (with tag)
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openApprovalDialog(order, "REJECT")}
                                  className="text-red-600"
                                  data-testid={`reject-order-${order.order_id}`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Reject (with tag)
                                </DropdownMenuItem>
                              </>
                            )}
                            {canApproveOrders && order.status === "APPROVED" && (
                              <DropdownMenuItem
                                onClick={() => openApprovalDialog(order, "APPROVE")}
                                className="text-purple-600"
                                data-testid={`dispatch-order-${order.order_id}`}
                              >
                                <Truck className="w-4 h-4 mr-2" />
                                Mark Dispatched (with tag)
                              </DropdownMenuItem>
                            )}
                            {canApproveOrders && order.status === "DISPATCHED" && (
                              <DropdownMenuItem
                                onClick={() => openApprovalDialog(order, "APPROVE")}
                                className="text-emerald-600"
                                data-testid={`deliver-order-${order.order_id}`}
                              >
                                <Package className="w-4 h-4 mr-2" />
                                Mark Delivered (with tag)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}

      {/* Create Order Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle>Create New Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Select Dealer</label>
                <Select
                  value={formData.party_id}
                  onValueChange={(value) => {
                    const party = parties.find((p) => p.party_id === value);
                    const defaultAddr = party?.delivery_addresses?.find(a => a.is_default)?.address || party?.address || "";
                    setFormData({
                      ...formData,
                      party_id: value,
                      delivery_address: defaultAddr,
                    });
                  }}
                >
                  <SelectTrigger data-testid="select-party">
                    <SelectValue placeholder="Select dealer" />
                  </SelectTrigger>
                  <SelectContent>
                    {parties
                      .filter((p) => p.party_type === "DEALER")
                      .map((party) => (
                        <SelectItem key={party.party_id} value={party.party_id}>
                          {party.firm_name} ({party.party_code})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-slate-700">Delivery Address</label>
                {(() => {
                  const selectedDealer = parties.find(p => p.party_id === formData.party_id);
                  const deliveryAddrs = selectedDealer?.delivery_addresses || [];
                  if (deliveryAddrs.length > 0) {
                    return (
                      <Select
                        value={formData.delivery_address}
                        onValueChange={(v) => setFormData({ ...formData, delivery_address: v })}
                      >
                        <SelectTrigger data-testid="select-delivery-address">
                          <SelectValue placeholder="Select delivery address" />
                        </SelectTrigger>
                        <SelectContent>
                          {deliveryAddrs.map((addr, i) => (
                            <SelectItem key={i} value={addr.address || `addr-${i}`}>
                              {addr.label}: {addr.address}
                            </SelectItem>
                          ))}
                          {selectedDealer?.address && (
                            <SelectItem value={selectedDealer.address}>
                              Business Address: {selectedDealer.address}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    );
                  }
                  return (
                    <Input
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                      placeholder="Delivery address"
                      data-testid="input-address"
                    />
                  );
                })()}
              </div>
            </div>

            {/* Target Unit Selection */}
            <div>
              <label className="text-sm font-medium text-slate-700">Target Factory Unit</label>
              <Select value={formData.target_unit || "unit_1"} onValueChange={v => setFormData({ ...formData, target_unit: v })}>
                <SelectTrigger data-testid="select-target-unit"><SelectValue placeholder="Select factory unit" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit_1">Unit 1 - Main Plant</SelectItem>
                  <SelectItem value="unit_2">Unit 2 - Secondary Plant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-700">Order Items</label>
              {formData.items.map((item, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Select
                      value={item.product_id}
                      onValueChange={(value) => updateItem(index, "product_id", value)}
                    >
                      <SelectTrigger data-testid={`select-product-${index}`}>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.product_id} value={product.product_id}>
                            {product.product_name} - {formatCurrency(product.base_price)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) => updateItem(index, "qty", e.target.value)}
                      placeholder="Qty"
                      data-testid={`input-qty-${index}`}
                    />
                  </div>
                  {formData.items.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(index)}
                      className="text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-order">
                Create Order
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Order ID</p>
                  <p className="font-mono font-medium">{viewingOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium">{viewingOrder.order_date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  {getStatusBadge(viewingOrder.status)}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Amount</p>
                  <p className="font-bold text-[#8B1A1A]">
                    {formatCurrency(viewingOrder.total_amount)}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Items</p>
                <div className="space-y-2">
                  {viewingOrder.items?.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-2 bg-slate-50 rounded"
                    >
                      <div>
                        <p className="font-medium text-sm">{item.product_name || item.sku}</p>
                        <p className="text-xs text-slate-500">Qty: {item.qty}</p>
                      </div>
                      <p className="font-mono">{formatCurrency(item.line_total)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span>{formatCurrency(viewingOrder.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">GST (CGST + SGST)</span>
                  <span>{formatCurrency(viewingOrder.cgst + viewingOrder.sgst)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-[#8B1A1A]">{formatCurrency(viewingOrder.total_amount)}</span>
                </div>
              </div>

              {/* Order Tags Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <Tag className="w-4 h-4" /> Issue Tags ({orderTags.length})
                  </h4>
                  <Button size="sm" variant="outline" onClick={() => setShowTagForm(!showTagForm)} className="h-7 text-xs" data-testid="toggle-tag-form-btn">
                    {showTagForm ? <X className="w-3 h-3 mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                    {showTagForm ? "Cancel" : "Add Tag"}
                  </Button>
                </div>

                {showTagForm && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2 mb-3" data-testid="tag-form">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Issue Type</label>
                      <Select value={tagForm.label} onValueChange={(v) => setTagForm({ ...tagForm, label: v })}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="tag-label-select">
                          <SelectValue placeholder="Select issue..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TAG_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Note</label>
                      <Input value={tagForm.note} onChange={(e) => setTagForm({ ...tagForm, note: e.target.value })} placeholder="Optional note..." className="mt-1 h-8 text-xs" data-testid="tag-note-input" />
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 px-2 py-1.5 border border-dashed border-slate-300 rounded cursor-pointer hover:border-slate-400 text-xs text-slate-500">
                        <Camera className="w-3.5 h-3.5" />
                        {tagPhotos.length > 0 ? `${tagPhotos.length} file(s)` : "Attach photos"}
                        <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => setTagPhotos(Array.from(e.target.files || []))} />
                      </label>
                    </div>
                    <Button size="sm" onClick={addTag} className="w-full h-7 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="submit-tag-btn">Add Issue Tag</Button>
                  </div>
                )}

                <div className="space-y-2">
                  {orderTags.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">No tags on this order</p>
                  ) : (
                    orderTags.map((tag) => (
                      <div key={tag.tag_id} className={`flex items-start justify-between p-2 rounded-lg border text-xs ${tag.resolved ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid={`tag-${tag.tag_id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5">
                            {tag.resolved ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
                            <span className={`font-medium ${tag.resolved ? "text-emerald-700 line-through" : "text-red-700"}`}>{tag.label}</span>
                          </div>
                          {tag.note && <p className="text-slate-500 mt-0.5 ml-5">{tag.note}</p>}
                          <p className="text-slate-400 mt-0.5 ml-5">by {tag.created_by_name} &middot; {new Date(tag.created_at).toLocaleDateString("en-IN")}</p>
                          {tag.resolved && <p className="text-emerald-600 mt-0.5 ml-5">Resolved by {tag.resolved_by}</p>}
                        </div>
                        {!tag.resolved && (
                          <Button size="sm" variant="ghost" onClick={() => resolveTag(tag.tag_id)} className="h-6 text-[10px] text-emerald-700 hover:bg-emerald-100" data-testid={`resolve-tag-${tag.tag_id}`}>
                            <Check className="w-3 h-3 mr-0.5" /> Resolve
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order Timeline Dialog */}
      <OrderTimeline orderId={timelineOrderId} onClose={() => setTimelineOrderId(null)} />

      <OrderDetailDialog
        orderId={detailOrderId}
        onClose={() => setDetailOrderId(null)}
        onStatusChange={() => fetchData({ silent: true })}
        onRequestAction={(order, action) => {
          setDetailOrderId(null);
          setApprovalDialog({ order, action });
          setApprovalForm({ tag: "", notes: "" });
        }}
      />

      {/* Approval Action Dialog (Approve / Reject / Hold with reason tag) */}
      <Dialog open={!!approvalDialog} onOpenChange={(o) => { if (!o) setApprovalDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="approval-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {approvalDialog?.action === "APPROVE" && "Approve Order"}
              {approvalDialog?.action === "REJECT" && "Reject Order"}
              {approvalDialog?.action === "HOLD" && "Put Order On Hold"}
            </DialogTitle>
          </DialogHeader>
          {approvalDialog && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">
                Order <strong>{approvalDialog.order.order_id}</strong> · Dealer: {approvalDialog.order.party_name || approvalDialog.order.party_id}
              </p>
              <div>
                <label className="text-xs font-medium text-slate-700">Reason Tag <span className="text-red-500">*</span></label>
                <Select value={approvalForm.tag} onValueChange={(v) => setApprovalForm({ ...approvalForm, tag: v })}>
                  <SelectTrigger className="mt-1" data-testid="approval-tag-select">
                    <SelectValue placeholder="Select reason tag..." />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVAL_REASON_TAGS.map(t => <SelectItem key={t} value={t} data-testid={`approval-tag-opt-${t.replace(/\s/g, '-').toLowerCase()}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
                <Input
                  value={approvalForm.notes}
                  onChange={(e) => setApprovalForm({ ...approvalForm, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="mt-1"
                  data-testid="approval-notes-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setApprovalDialog(null)}>Cancel</Button>
                <Button
                  onClick={submitApprovalAction}
                  disabled={approvalSaving || !approvalForm.tag}
                  className={
                    approvalDialog.action === "APPROVE" ? "bg-emerald-600 hover:bg-emerald-700 text-white" :
                    approvalDialog.action === "REJECT" ? "bg-red-600 hover:bg-red-700 text-white" :
                    "bg-amber-600 hover:bg-amber-700 text-white"
                  }
                  data-testid="approval-submit-btn"
                >
                  {approvalSaving ? "Saving..." : `Confirm ${approvalDialog.action}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
